import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Zap, Bell } from 'lucide-react';
import { useSite } from '../context/SiteContext';
import UpcomingCouponCard from '../components/UpcomingCouponCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusRank = (c) => {
  const now = Date.now();
  const avail = c.available_at ? new Date(c.available_at).getTime() : null;
  let s;
  if (c.manual_status === 'finalizado') s = 'finalizado';
  else if (c.manual_status === 'disponible') s = 'disponible';
  else if (c.manual_status === 'proximo') s = 'proximo';
  else s = avail && now >= avail ? 'disponible' : 'proximo';
  return { disponible: 0, proximo: 1, finalizado: 2 }[s];
};

const ProximosCupones = () => {
  const { settings, loading: siteLoading } = useSite();
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!siteLoading && settings && settings.upcoming_enabled === false) {
      navigate('/', { replace: true });
    }
  }, [siteLoading, settings, navigate]);

  useEffect(() => {
    const fetchData = async (initial = false) => {
      try {
        const res = await axios.get(`${API}/upcoming-coupons`);
        setCoupons(res.data || []);
      } catch {
        if (initial) setCoupons([]);
      } finally {
        if (initial) setLoading(false);
      }
    };
    fetchData(true);
    const iv = setInterval(() => fetchData(false), 60000); // auto-refresh list
    return () => clearInterval(iv);
  }, []);

  const sorted = [...coupons].sort((a, b) => {
    const r = statusRank(a) - statusRank(b);
    if (r !== 0) return r;
    return (a.available_at || '9999').localeCompare(b.available_at || '9999');
  });

  return (
    <div className="min-h-screen bg-[#FFD400] pb-24" data-testid="proximos-cupones-page">
      {/* Banner */}
      <div className="px-4 pt-6 pb-8 text-center">
        <div className="inline-flex items-center gap-2 bg-[#1479FF] text-white px-4 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wide mb-3">
          <Zap className="w-4 h-4" /> Nuevos
        </div>
        <h1 className="font-fredoka text-4xl sm:text-5xl font-black text-slate-900 leading-none" data-testid="proximos-cupones-title">
          Próximos cupones
        </h1>
        <p className="text-slate-800/80 font-semibold text-sm sm:text-base mt-2 max-w-md mx-auto">
          Cupones que se liberan a una hora exacta. ¡Actívalos a tiempo y aprovecha el descuento!
        </p>
      </div>

      {/* Content */}
      <div className="px-4 pt-2 pb-6">
        {/* Fixed WhatsApp notice */}
        <div className="max-w-5xl mx-auto mb-4">
          <div className="bg-white rounded-2xl p-4 shadow-md flex flex-col sm:flex-row items-center gap-3" data-testid="upcoming-whatsapp-notice">
            <div className="flex-1 text-center sm:text-left">
              <p className="font-bold text-slate-900 flex items-center gap-1.5 justify-center sm:justify-start">
                <Bell className="w-4 h-4 text-amber-500" /> No te pierdas los próximos cupones
              </p>
              <p className="text-sm text-slate-600 mt-0.5">
                Te avisaremos por nuestro canal de WhatsApp cuando estén disponibles. Activa las notificaciones para no perderte ninguno.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-5xl mx-auto">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-40 rounded-[22px] bg-white/40 animate-pulse" />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-16">
            <Zap className="w-12 h-12 text-slate-800/40 mx-auto mb-3" />
            <p className="text-slate-900 font-bold">Aún no hay próximos cupones.</p>
            <p className="text-slate-800/70 text-sm">¡Vuelve pronto para no perderte las liberaciones!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-5xl mx-auto">
            {sorted.map((c) => (
              <UpcomingCouponCard key={c.id} coupon={c} settings={settings} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProximosCupones;
